// The keyboard source (P2.8) — Architecture §3's second input source, and the one that proves the
// seam is real.
//
// §3 promises that a MIDI source at FF-1 "emits the same ControlEvents and reuses the entire mapping
// layer". A promise with one source behind it is untested, which is what P2.8's rule means by "if
// [the keyboard] needs a private path the seam is wrong". This file therefore does exactly one
// thing: turn key events into `ControlEvent`s and hand them to the bus. It knows nothing about the
// transport, the deck, or audio — `transport-controls.ts` owns what a control *means*, and this owns
// only which key produces it.
//
// §3 also names this file as the home of one specific piece of knowledge: "the keyboard source owns
// ghosting-aware layout logic in one place". With two keys there was no ghosting to be aware of;
// with P3.7's eight there is a real cross-deck chord, and the bindings table below is where FR-39's
// rollover-conscious layout lives. Its note is the design rule, and the test checks the rule rather
// than the eight rows it currently produces.

import { controlEvent, type ControlBus } from '../bus';
import {
  controlIdInNamespace,
  type ControlId,
  type DeckId,
} from '../../platform/types/control';

export interface KeyBinding {
  /** `KeyboardEvent.code` — physical position, not the character produced. */
  readonly code: string;
  readonly controlId: ControlId;
  /**
   * True when holding the key holds the control down (cue), false when a press is a trigger (play).
   *
   * Momentary bindings emit value 1 on keydown and value 0 on keyup. Trigger bindings emit only the
   * 1 — a release that meant something would make Space a hold, and Space is not a hold.
   */
  readonly momentary: boolean;
  /** For FR-40's cheat sheet, so the overlay reads the same table the handler does. */
  readonly label: string;
}

/**
 * The default layout. **Data, and every choice in it is FR-39's rollover rule, not taste.**
 *
 * FR-39: "Default keyboard layout designed around common key-rollover limits (chords spread across
 * rows/hands)". Cheap USB keyboards drop the third simultaneous key in a matrix row, and a DJ holds
 * cue on one deck while pressing play on the other — so the two decks' controls must not share a
 * row or a hand.
 *
 * **Deck A is the left hand, deck B is the right** (P3.7 fills in the half P2.8 reserved):
 *
 * | | play | cue | nudge − | nudge + |
 * | ------ | ------- | ---- | ------- | ------- |
 * | deck A | `Space` | `Q`  | `W`     | `E`     |
 * | deck B | `J`     | `P`  | `I`     | `O`     |
 *
 * `Space` for deck A's play, because every transport in every application is Space and fighting that
 * would be perverse. There is no second space bar, so deck B's play is `J` — right index finger, on
 * the home key with the tactile bump on it. That is the closest a keyboard gets to Space's actual
 * property: the one key a hand can find without looking, which is what a play button needs to be.
 *
 * ⚠️ **`L` was the first choice and the test below rejected it**, which is the finger rule earning
 * its place within minutes of being written: `L` and `O` are both the right ring finger, and deck
 * B's nudge is on `O`. The same class of mistake P3.6 made with `A`/`Q` and caught by memory.
 *
 * **Two rules decided the right hand, and both were already written down.** The first is FR-39's:
 * the cross-deck chord a DJ actually plays is cue-one-deck-while-playing-the-other, and `Q`+`L` and
 * `P`+`Space` are both two hands. The second came from P3.6, which rejected `A` for deck A's nudge
 * because `A` sits under the same finger as `Q` and cue is *held* while nudging — so no two of one
 * deck's controls may share a finger. That rules out `Semicolon` for deck B's play (right pinky,
 * same finger as `P`) exactly as it ruled out `A`, and `keyboard-source.test.ts` now checks it as a
 * rule rather than as two remembered anecdotes.
 *
 * The nudge pair reads **left-to-right as slower-to-faster on both decks** (`W`→`E`, `I`→`O`) rather
 * than mirroring by finger. A mirror would put deck B's "faster" on the left of its "slower", and
 * the thing a hand learns here is the direction on the desk, not which finger it was.
 *
 * Not a `Map` keyed by code, deliberately: FR-39's remapping UI has to detect *conflicts*, which
 * means it needs to see two bindings claiming one key rather than have the second silently win.
 */
export const DEFAULT_KEY_BINDINGS: readonly KeyBinding[] = [
  {
    code: 'Space',
    controlId: 'deck.a.play',
    momentary: false,
    label: 'Play / pause deck A',
  },
  {
    code: 'KeyQ',
    controlId: 'deck.a.cue',
    momentary: true,
    label: 'Cue deck A (hold to preview)',
  },
  // FR-05's nudge (P3.6). **Momentary, which is the whole point**: a nudge is a bend held against a
  // beat for a second and released, so the keyup matters as much as the keydown — the same shape as
  // cue, and the reason `momentary` is a field in this table rather than a branch in the source.
  //
  // `W` and `E` rather than the `A`/`D` a game would use, and the existing rollover test is what
  // said so: `A` sits under the same finger as `Q`, and cue is held while nudging far more often
  // than the reverse. These keep deck A's controls on the left hand — Space under the thumb, cue on
  // the pinky, nudge on the ring and middle fingers — with the left-to-right pair reading as
  // slower-to-faster, and they leave the right hand entirely free for deck B at P3.7.
  {
    code: 'KeyW',
    controlId: 'deck.a.nudge.down',
    momentary: true,
    label: 'Nudge deck A slower (hold)',
  },
  {
    code: 'KeyE',
    controlId: 'deck.a.nudge.up',
    momentary: true,
    label: 'Nudge deck A faster (hold)',
  },

  // Deck B (P3.7), on the hand P2.8 left free. Same four controls, same `momentary` flags, mirrored
  // onto the right hand — see the table above for why each key is the one it is.
  {
    code: 'KeyJ',
    controlId: 'deck.b.play',
    momentary: false,
    label: 'Play / pause deck B',
  },
  {
    code: 'KeyP',
    controlId: 'deck.b.cue',
    momentary: true,
    label: 'Cue deck B (hold to preview)',
  },
  {
    code: 'KeyI',
    controlId: 'deck.b.nudge.down',
    momentary: true,
    label: 'Nudge deck B slower (hold)',
  },
  {
    code: 'KeyO',
    controlId: 'deck.b.nudge.up',
    momentary: true,
    label: 'Nudge deck B faster (hold)',
  },
];

/**
 * The bindings for one deck (P3.7).
 *
 * Each deck's `KeyboardSource` gets only its own, which is not merely tidy: the source is
 * constructed per deck alongside a bus that only that deck subscribes to, so an unfiltered source
 * would publish `deck.b.play` onto deck A's bus where nothing listens — harmless, and it would also
 * put deck B's four keys in the hint list printed under deck A. The filter is what keeps the printed
 * hints and the live bindings the same table, which is the whole reason they are read from here.
 *
 * A prefix match on the id rather than a second column in the table: the id already carries the
 * deck, and a `deck` field beside a `deck.b.play` control id is a second copy of one fact.
 */
export function bindingsForDeck(
  deck: DeckId,
  bindings: readonly KeyBinding[] = DEFAULT_KEY_BINDINGS,
): readonly KeyBinding[] {
  return bindings.filter((binding) =>
    controlIdInNamespace(binding.controlId, `deck.${deck}`),
  );
}

/**
 * Should this key event be ignored because the user is typing?
 *
 * There is no text field in the app today and this is still not premature: FR-39's remapping UI and
 * FR-34's library search are both search boxes, and a Space that plays a track while someone types
 * a track name is the kind of bug that ships because the guard was added after the field was.
 *
 * **Duck-typed rather than `instanceof HTMLElement`, and that is a correctness choice as well as a
 * testability one.** `instanceof` is per-realm: an element from an embedded document fails the check
 * against *this* realm's `HTMLElement` even though it is unquestionably an input. Reading the
 * properties the DOM actually exposes works across realms, and works in a test with no DOM at all.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || typeof target !== 'object') {
    return false;
  }
  const element = target as { tagName?: unknown; isContentEditable?: unknown };
  if (element.isContentEditable === true) {
    return true;
  }
  const tag = typeof element.tagName === 'string' ? element.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Is this a key event? Duck-typed, for the same cross-realm reason as above.
 *
 * `code` is the field this source is built on — physical key position rather than the character
 * produced, so a binding survives a Dvorak or AZERTY layout.
 */
function asKeyEvent(event: Event): {
  readonly code: string;
  readonly repeat: boolean;
  readonly target: EventTarget | null;
} | null {
  const candidate = event as unknown as {
    code?: unknown;
    repeat?: unknown;
    target?: unknown;
  };
  if (typeof candidate.code !== 'string') {
    return null;
  }
  return {
    code: candidate.code,
    repeat: candidate.repeat === true,
    target: (candidate.target ?? null) as EventTarget | null,
  };
}

export interface KeyboardSourceOptions {
  readonly bus: ControlBus;
  /** AudioContext time, for `ControlEvent.timestamp`. A function because the context may not exist
   * yet when the source is attached — the deck is built on the first load, not at mount. */
  readonly now: () => number;
  readonly bindings?: readonly KeyBinding[];
  /**
   * Where `blur` and `visibilitychange` are listened for — the window, in a browser.
   *
   * Injectable because those two events are the *only* reason this class would otherwise need a
   * global, and a class that reaches for `globalThis` cannot be tested without faking one. Defaults
   * to `globalThis` when it looks like an EventTarget, which it does in every browser and in a
   * worker, and does not under plain Node.
   */
  readonly globalTarget?: EventTarget | null;
}

/** `globalThis`, but only if it can actually take listeners. Node's cannot. */
function defaultGlobalTarget(): EventTarget | null {
  const candidate = globalThis as unknown as { addEventListener?: unknown };
  return typeof candidate.addEventListener === 'function' ? globalThis : null;
}

export class KeyboardSource {
  readonly #bus: ControlBus;
  readonly #now: () => number;
  readonly #bindings: readonly KeyBinding[];
  readonly #globalTarget: EventTarget | null;

  /**
   * Codes currently held down.
   *
   * **This is the "held keys do not retrigger" guarantee**, and `event.repeat` alone is not enough
   * to provide it. `repeat` is the browser's own flag and it is reliable in the common case, but it
   * is a property of the *event stream*, and the stream can be interrupted: switching windows
   * mid-hold delivers no keyup at all, so the next keydown after coming back would arrive with
   * `repeat: false` on a key the source still believed was down. Tracking the set makes the
   * invariant ours — one press, one event, always — and `#releaseAll` on blur is what keeps the set
   * honest across that interruption.
   */
  readonly #held = new Set<string>();
  #target: EventTarget | null = null;

  constructor(options: KeyboardSourceOptions) {
    this.#bus = options.bus;
    this.#now = options.now;
    this.#bindings = options.bindings ?? DEFAULT_KEY_BINDINGS;
    this.#globalTarget =
      options.globalTarget === undefined ? defaultGlobalTarget() : options.globalTarget;
  }

  attach(target: EventTarget): void {
    this.detach();
    this.#target = target;
    target.addEventListener('keydown', this.#onKeyDown);
    target.addEventListener('keyup', this.#onKeyUp);
    // A held key whose keyup never arrives is a deck stuck in preview. Both of these fire when the
    // page loses focus mid-hold — `blur` for window focus, `visibilitychange` for tab switches —
    // and each releases everything rather than trying to guess which keys survived.
    this.#globalTarget?.addEventListener('blur', this.#releaseAll);
    this.#globalTarget?.addEventListener('visibilitychange', this.#releaseAll);
  }

  detach(): void {
    const target = this.#target;
    if (target === null) {
      return;
    }
    // Release before unbinding, or a source detached mid-hold leaves the deck previewing with
    // nothing left listening for the key that would stop it.
    this.#releaseAll();
    target.removeEventListener('keydown', this.#onKeyDown);
    target.removeEventListener('keyup', this.#onKeyUp);
    this.#globalTarget?.removeEventListener('blur', this.#releaseAll);
    this.#globalTarget?.removeEventListener('visibilitychange', this.#releaseAll);
    this.#target = null;
  }

  /** Which keys the source currently believes are down. For tests, and for FR-40's overlay to
   * highlight a held key. */
  get heldCodes(): readonly string[] {
    return [...this.#held];
  }

  #bindingFor(code: string): KeyBinding | undefined {
    return this.#bindings.find((binding) => binding.code === code);
  }

  #emit(binding: KeyBinding, value: number): void {
    this.#bus.publish(controlEvent(binding.controlId, value, this.#now(), 'keyboard'));
  }

  // Arrow fields, not methods: `addEventListener` and `removeEventListener` must be handed the same
  // function reference, and a `.bind()` per call would remove nothing.
  readonly #onKeyDown = (event: Event): void => {
    const key = asKeyEvent(event);
    if (key === null || isTypingTarget(key.target)) {
      return;
    }

    const binding = this.#bindingFor(key.code);
    if (binding === undefined) {
      return;
    }

    // Space scrolls the page and also activates whichever button last had focus — which after
    // clicking Play is the Play button, so without this a single Space would fire the transport
    // twice. Called before the repeat check so a held Space does not scroll either.
    event.preventDefault();

    if (key.repeat || this.#held.has(key.code)) {
      return;
    }
    this.#held.add(key.code);
    this.#emit(binding, 1);
  };

  readonly #onKeyUp = (event: Event): void => {
    const key = asKeyEvent(event);
    if (key === null) {
      return;
    }
    const binding = this.#bindingFor(key.code);
    if (binding === undefined) {
      return;
    }

    event.preventDefault();
    // Unconditionally forget the key, even if the binding is a trigger and emits nothing — the set
    // is the source's memory of the physical keyboard, not of what it published.
    const wasHeld = this.#held.delete(key.code);
    if (wasHeld && binding.momentary) {
      this.#emit(binding, 0);
    }
  };

  readonly #releaseAll = (): void => {
    for (const code of [...this.#held]) {
      this.#held.delete(code);
      const binding = this.#bindingFor(code);
      if (binding?.momentary === true) {
        this.#emit(binding, 0);
      }
    }
  };
}
